import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { API_BASE_URL, getUploadUrl } from "@/utils/api";

const MySwal = withReactContent(Swal);

// --- 1. INTERFACE ---
interface Course {
  unique_id?: string;
  user_id: number;
  user_name: string;
  email: string;
  level: string;
  package: string;
  tutor_name?: string;
  amount?: number;
  session_count?: number;
  content_material?: string;
  feedback?: string;
  status: "Active" | "Completed" | "Inactive";
  certificate_file: string | null;
  completed_at: string | null;
  order_id?: number;
  course_id?: number;
  order_status?: string;
  join_date?: string;
}

// --- 2. HELPER UTILS ---
const getAvatarColor = (name: string) => {
  const colors = [
    "#FF6B6B",
    "#4ECDC4",
    "#45B7D1",
    "#96CEB4",
    "#FFEEAD",
    "#D4A5A5",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

const getInitials = (name: string) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .substring(0, 2)
    .toUpperCase();

const formatRupiah = (number: number) => {
  return new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    minimumFractionDigits: 0,
  }).format(number);
};

// --- 3. STYLES (CSS MODERN, COMPACT & EDITABLE) ---
const courseStyles = `
  :root { --bg-soft: #f4f7f6; --primary-red: #e53935; --text-dark: #2d3436; }
  .course-container { padding: 20px; background: var(--bg-soft); border-radius: 20px; min-height: 400px; font-family: 'Poppins', sans-serif; }
  .course-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 30px; }
  .glass-search { background: white; border-radius: 50px; padding: 8px 20px; box-shadow: 0 4px 15px rgba(0,0,0,0.03); border: 1px solid rgba(0,0,0,0.02); display: flex; align-items: center; width: 280px; }
  .glass-search input { border: none; outline: none; width: 100%; font-size: 0.95rem; }
  .modern-table { width: 100%; border-collapse: separate; border-spacing: 0 15px; }
  .modern-table thead th { font-size: 0.85rem; text-transform: uppercase; color: #b2bec3; font-weight: 700; padding: 0 20px 10px 20px; text-align: left; }
  .course-row td { background: white; padding: 20px; vertical-align: middle; border-top: 1px solid rgba(0,0,0,0.01); border-bottom: 1px solid rgba(0,0,0,0.01); }
  .course-row td:first-child { border-top-left-radius: 16px; border-bottom-left-radius: 16px; }
  .course-row td:last-child { border-top-right-radius: 16px; border-bottom-right-radius: 16px; }
  .badge-capsule { padding: 5px 12px; border-radius: 20px; font-size: 0.75rem; font-weight: 700; display: inline-block; }
  .bg-basic { background: #e3f2fd; color: #1976d2; }
  .bg-indep { background: #fff3e0; color: #f57c00; }
  .bg-prof { background: #e8f5e9; color: #2e7d32; }
  .bg-none { background: #f5f5f5; color: #757575; }
  
  /* TOMBOL AKSI */
  .action-btn { width: 36px; height: 36px; border-radius: 10px; border: none; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: 0.2s; font-size: 0.9rem; margin-right: 5px;}
  .btn-upload { background: #e3f2fd; color: #1976d2; }
  .btn-upload:hover { background: #1976d2; color: white; }
  .btn-delete { background: #ffebee; color: #c62828; }
  .btn-delete:hover { background: #c62828; color: white; }

  /* FORM STYLES SHARED */
  .swal-label { font-weight:700; font-size:0.85rem; color:#2d3436; display:block; text-align:left; margin-bottom:5px; }
  .swal-input-week { border: 2px solid #e53935 !important; font-weight:bold; color:#2d3436; }
  .swal-input-custom { width:100% !important; margin:0 0 10px 0 !important; height:40px !important; font-size:0.9rem !important; border:1px solid #dfe6e9 !important; border-radius:4px; }
  .badge-form { position:absolute; top:-10px; left:15px; background:#e53935; color:white; font-size:0.7rem; padding:4px 12px; border-radius:20px; font-weight:bold; text-transform:uppercase; letter-spacing:0.5px; }

  /* --- STYLE UPLOAD (COMPACT 2 KOLOM) --- */
  .upload-grid-container {
    display: grid; grid-template-columns: 1fr 1fr; gap: 20px; text-align: left; margin-top: 10px;
  }
  .upload-col-left { padding-right: 15px; border-right: 1px dashed #dfe6e9; }
  .upload-col-right { padding-left: 5px; display: flex; flex-direction: column; justify-content: flex-start; }
  
  .compact-input { height: 35px !important; font-size: 0.85rem !important; margin-bottom: 8px !important; }
  .compact-label { font-size: 0.75rem; font-weight: 700; color: #636e72; margin-bottom: 3px; display: block; }

  #list-up-container { background: #f8f9fa; border: 1px solid #dfe6e9; border-radius: 6px; height: 180px; overflow-y: auto; padding: 10px; margin-bottom: 15px; }

  .mini-item { background: white; border-bottom: 1px solid #eee; padding: 8px; display: flex; justify-content: space-between; align-items: center; font-size: 0.8rem; }
  .btn-mini-del { background: #ff7675; color: white; border: none; border-radius: 4px; width: 20px; height: 20px; font-size: 0.7rem; cursor: pointer; display: flex; align-items: center; justify-content: center; }

  /* --- STYLE EDITABLE LIST (INPUT LANGSUNG) --- */
  .edit-list-item { background: white; border: 1px solid #e0e0e0; border-radius: 8px; padding: 10px; margin-bottom: 10px; display: flex; gap: 10px; align-items: flex-start; }
  .edit-inputs-wrapper { flex: 1; display: flex; flex-direction: column; gap: 5px; }
  .row-inputs { display: flex; gap: 8px; }
  
  .ghost-input { background: #f9f9f9; border: 1px solid #eee; padding: 5px 8px; border-radius: 4px; font-size: 0.85rem; color: #2d3436; font-family: 'Poppins', sans-serif; width: 100%; transition: 0.2s; }
  .ghost-input:focus { background: #fff; border-color: #0984e3; outline: none; }
  .ghost-textarea { resize: none; height: 50px; }
  .mini-label { font-size: 0.65rem; color: #b2bec3; font-weight: 700; margin-bottom: 2px; display: block; }
  .btn-del-item { border:none; background:none; color:#b2bec3; cursor:pointer; padding:5px; font-size: 1rem; }
  .btn-del-item:hover { color:#e74c3c; }
`;

const CourseContent: React.FC = () => {
  const [courses, setCourses] = useState<Course[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");

  // API URLs
  const BASE_URL = `${API_BASE_URL}/courses`;

  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem("token")}`,
  });

  const fetchCourses = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BASE_URL}/all`, {
        headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setCourses(
          data.data.map((c: any) => ({
            unique_id: c.unique_id,
            user_id: c.user_id,
            user_name: c.user_name,
            email: c.email,
            level: c.level,
            package: c.package,
            tutor_name: c.tutor_name,
            amount: c.amount,
            session_count: c.session_count,
            content_material: c.content_material,
            feedback: c.feedback || "",
            course_id: c.course_id || 0,
            certificate_file: c.certificate_file,
            status: c.status,
          })),
        );
      } else {
        setCourses([]);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, []);

  // --- 1. FITUR QUICK EDIT (EDITABLE LIST & UPLOAD ULANG SERTIFIKAT) ---
  const quickUpdatePackage = async (
    userId: number,
    userName: string,
    currentData: any,
  ) => {
    MySwal.fire({ title: "Memuat data...", didOpen: () => Swal.showLoading() });

    let existingMaterials: any[] = [];
    try {
      if (currentData.course_id) {
        const res = await fetch(
          `${BASE_URL}/materials/${currentData.course_id}`,
          { headers: getAuthHeaders() },
        );
        const json = await res.json();
        if (json.success) existingMaterials = json.data;
      }
    } catch (e) {
      console.error("Gagal load materi", e);
    }

    const { value: formData } = await MySwal.fire({
      title: `Edit Data: ${userName}`,
      width: "850px",
      html: `
        <div style="text-align:left; font-family: 'Poppins', sans-serif;">
          
          <div style="border-bottom: 2px dashed #b2bec3; padding-bottom:10px; margin-bottom:15px;">
             <label class="swal-label" style="color:#e53935; font-size:1rem;">1. EDIT MATERI & SESI</label>
             <small style="color:#636e72;">Klik pada teks di dalam kotak untuk mengedit.</small>
          </div>

          <div id="edit-list-container" style="background:#f1f2f6; padding:10px; border-radius:8px; max-height:300px; overflow-y:auto; margin-bottom:15px;">
             </div>

          <div style="text-align:center; margin-bottom:25px;">
              <button type="button" id="btn-add-empty-row" style="background:#2d3436; color:white; border:none; padding:8px 15px; border-radius:6px; cursor:pointer; font-size:0.85rem; font-weight:bold;">
                <i class="fas fa-plus"></i> Tambah Baris Kosong
              </button>
          </div>

          <div style="display:grid; grid-template-columns: 1fr 1fr; gap:20px; border-top: 1px solid #eee; padding-top:20px;">
              
              <div>
                  <label class="swal-label">2. Pesan / Catatan Tutor</label>
                  <textarea id="swal-message" class="swal2-textarea" style="width:100% !important; margin:0 !important; height:100px !important; font-size:0.9rem !important; padding:10px; border:1px solid #74b9ff;" placeholder="Feedback untuk siswa...">${currentData.feedback || ""}</textarea>
              </div>

              <div>
                   <label class="swal-label" style="color:#0984e3;">3. Update Sertifikat (Opsional)</label>
                   <div style="background:#e3f2fd; padding:15px; border-radius:8px; border:1px dashed #0984e3; text-align:center;">
                       <i class="fas fa-file-pdf" style="font-size:2rem; color:#0984e3; margin-bottom:10px;"></i>
                       <p style="font-size:0.8rem; margin:0 0 10px 0; color:#636e72;">
                           Status File: <b>${currentData.certificate_file ? "Sudah Ada" : "Belum Ada"}</b><br>
                           Upload file baru untuk mengganti.
                       </p>
                       <input type="file" id="swal-file-edit" accept="application/pdf, image/*" style="font-size:0.8rem; width:100%;" />
                   </div>
              </div>
          </div>

        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Simpan Perubahan",
      confirmButtonColor: "#0984e3",
      focusConfirm: false,
      didOpen: () => {
        const container = document.getElementById("edit-list-container");
        const btnAdd = document.getElementById("btn-add-empty-row");

        const renderEditableItem = (
          week: string,
          type: string,
          title: string,
          content: string,
        ) => {
          const itemDiv = document.createElement("div");
          itemDiv.className = "edit-list-item";
          itemDiv.innerHTML = `
                <div class="edit-inputs-wrapper">
                    <div class="row-inputs">
                        <div style="flex:1"><label class="mini-label">MINGGU</label><input class="ghost-input input-week" value="${week}" placeholder="Week 1"></div>
                        <div style="flex:1"><label class="mini-label">TIPE</label><input class="ghost-input input-type" value="${type}" placeholder="Quiz/Materi"></div>
                    </div>
                    <div><label class="mini-label">JUDUL MATERI</label><input class="ghost-input input-title" value="${title}" placeholder="Judul materi..."></div>
                    <div><label class="mini-label">DESKRIPSI</label><textarea class="ghost-input ghost-textarea input-content" placeholder="Deskripsi...">${content}</textarea></div>
                </div>
                <button type="button" class="btn-del-item" style="margin-top:25px;" title="Hapus Baris"><i class="fas fa-trash-alt"></i></button>
            `;
          itemDiv
            .querySelector(".btn-del-item")!
            .addEventListener("click", () => itemDiv.remove());
          container?.appendChild(itemDiv);
        };

        if (existingMaterials.length > 0) {
          existingMaterials.forEach((m) =>
            renderEditableItem(
              m.week_title,
              m.material_type,
              m.title,
              m.content,
            ),
          );
        } else {
          container!.innerHTML =
            '<p style="text-align:center; color:#b2bec3; font-size:0.8rem; margin-top:20px;" id="no-data-msg">Belum ada materi.</p>';
        }

        if (btnAdd) {
          btnAdd.onclick = () => {
            const msg = document.getElementById("no-data-msg");
            if (msg) msg.remove();
            renderEditableItem("", "", "", "");
            if (container) container.scrollTop = container.scrollHeight;
          };
        }
      },
      preConfirm: () => {
        // 1. Ambil List Materi
        const container = document.getElementById("edit-list-container");
        let materials: any[] = [];
        if (container) {
          container.querySelectorAll(".edit-list-item").forEach((item: any) => {
            const week = (item.querySelector(".input-week") as HTMLInputElement)
              .value;
            const type = (item.querySelector(".input-type") as HTMLInputElement)
              .value;
            const title = (
              item.querySelector(".input-title") as HTMLInputElement
            ).value;
            const content = (
              item.querySelector(".input-content") as HTMLTextAreaElement
            ).value;
            if (title.trim() !== "")
              materials.push({ week, type, title, content });
          });
        }

        // 2. Ambil File & Feedback
        const fileInput = document.getElementById(
          "swal-file-edit",
        ) as HTMLInputElement;
        const teacherMsg = (
          document.getElementById("swal-message") as HTMLTextAreaElement
        ).value;

        // 3. Buat FormData (PENTING: Karena ada upload file)
        const fd = new FormData();
        fd.append("course_id", String(currentData.course_id));
        fd.append("teacher_message", teacherMsg);
        fd.append("materials_json", JSON.stringify(materials));

        // Hanya append file jika user memilih file baru
        if (fileInput.files && fileInput.files.length > 0) {
          fd.append("certificate", fileInput.files[0]);
        }

        return fd;
      },
    });

    if (formData) {
      try {
        MySwal.fire({
          title: "Menyimpan...",
          didOpen: () => Swal.showLoading(),
        });

        // PENTING: Jangan set Content-Type header saat kirim FormData, biarkan browser yang atur boundary
        const headers: any = getAuthHeaders();
        delete headers["Content-Type"];

        const response = await fetch(`${BASE_URL}/user/${userId}/status`, {
          method: "PUT",
          headers: headers,
          body: formData,
        });

        const resJson = await response.json();
        if (response.ok && resJson.success) {
          MySwal.fire({
            icon: "success",
            title: "Berhasil!",
            text: "Data & Sertifikat diperbarui.",
            timer: 1500,
            showConfirmButton: false,
          });
          fetchCourses();
        } else throw new Error(resJson.message);
      } catch (err: any) {
        MySwal.fire({ icon: "error", title: "Gagal", text: err.message });
      }
    }
  };

  // --- 2. FITUR UPLOAD (COMPACT 2 KOLOM) ---
  const handleUploadClick = (
    userId: number,
    userName: string,
    courseId?: number,
  ) => {
    if (!courseId) {
      MySwal.fire("Error", "ID Kursus error", "error");
      return;
    }

    MySwal.fire({
      title: "Upload Materi & Sertifikat",
      width: "900px",
      padding: "1.5em",
      html: `
        <div class="upload-grid-container" style="font-family: 'Poppins', sans-serif;">
          
          <div class="upload-col-left">
             <div style="background:#e3f2fd; padding:8px 12px; border-radius:6px; margin-bottom:15px; color:#1976d2; font-size:0.85rem;">
                <i class="fas fa-user-graduate"></i> Siswa: <b>${userName}</b>
             </div>

             <div><label class="compact-label">1. Minggu / Topik Utama</label><input id="swal-week-up" class="swal2-input swal-input-custom compact-input" placeholder="Cth: Week 1 (Introduction)"></div>

             <div style="background:#fff; border:1px solid #e53935; padding:12px; border-radius:8px; position:relative; margin-top:15px;">
                  <span class="badge-form" style="font-size:0.65rem; top:-8px;">INPUT SESI BARU</span>
                  <div style="display:grid; grid-template-columns: 1fr 2fr; gap:10px;">
                    <div><label class="compact-label">Tipe</label><input id="swal-type-up" class="swal2-input swal-input-custom compact-input" placeholder="Quiz/Materi"></div>
                    <div><label class="compact-label">Judul</label><input id="swal-title-up" class="swal2-input swal-input-custom compact-input" placeholder="Judul Materi"></div>
                  </div>
                  <label class="compact-label">Deskripsi</label>
                  <textarea id="swal-content-up" class="swal2-textarea" style="width:100% !important; margin:0 0 10px 0!important; height:50px!important; font-size:0.85rem!important; border:1px solid #dfe6e9;" placeholder="Jelaskan sedikit..."></textarea>
                  
                  <button type="button" id="btn-add-up" style="width:100%; background:#2d3436; color:white; border:none; padding:8px; border-radius:6px; font-size:0.85rem; cursor:pointer; transition:0.2s;">
                    <i class="fas fa-plus-circle"></i> Tambahkan ke List Kanan
                  </button>
             </div>
          </div>

          <div class="upload-col-right">
             <div>
               <label class="compact-label">2. Daftar Materi (Preview)</label>
               <div id="list-up-container">
                  <p style="text-align:center; color:#b2bec3; font-size:0.8rem; margin-top:60px;">Belum ada materi ditambahkan.</p>
               </div>
             </div>

             <div>
                <label class="compact-label">3. Pesan Tutor (Opsional)</label>
                <textarea id="swal-msg-up" class="swal2-textarea" style="width:100%!important; height:60px!important; margin:0 0 10px 0!important; font-size:0.85rem!important; border:1px solid #74b9ff;" placeholder="Pesan untuk siswa..."></textarea>
                
                <label class="compact-label" style="color:#0984e3;">4. Upload File Sertifikat (PDF/Img)</label>
                <input type="file" id="file-up" accept="application/pdf, image/*" style="font-size:0.85rem; width:100%;" />
             </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Simpan & Upload",
      confirmButtonColor: "#00b894",
      cancelButtonText: "Batal",
      focusConfirm: false,
      didOpen: () => {
        const btnAdd = document.getElementById("btn-add-up");
        const container = document.getElementById("list-up-container");
        const inWeek = document.getElementById(
          "swal-week-up",
        ) as HTMLInputElement;
        const inType = document.getElementById(
          "swal-type-up",
        ) as HTMLInputElement;
        const inTitle = document.getElementById(
          "swal-title-up",
        ) as HTMLInputElement;
        const inContent = document.getElementById(
          "swal-content-up",
        ) as HTMLTextAreaElement;
        let isFirstItem = true;

        if (btnAdd && container) {
          btnAdd.onclick = () => {
            if (!inWeek.value) {
              Swal.showValidationMessage('Isi "Minggu / Topik" dulu!');
              return;
            }
            if (!inType.value || !inTitle.value) {
              Swal.showValidationMessage("Tipe dan Judul wajib diisi!");
              return;
            }
            if (isFirstItem) {
              container.innerHTML = "";
              isFirstItem = false;
            }

            const div = document.createElement("div");
            div.className = "mini-item";
            div.dataset.week = inWeek.value;
            div.dataset.type = inType.value;
            div.dataset.title = inTitle.value;
            div.dataset.content = inContent.value;
            div.innerHTML = `
                  <div style="display:flex; flex-direction:column;">
                    <span style="font-weight:bold; color:#2d3436;">${inType.value} - ${inTitle.value}</span>
                    <span style="font-size:0.7rem; color:#636e72;">${inWeek.value}</span>
                  </div>
                  <button type="button" class="btn-mini-del"><i class="fas fa-times"></i></button>
                `;
            div
              .querySelector(".btn-mini-del")!
              .addEventListener("click", () => {
                div.remove();
                if (container.children.length === 0) {
                  container.innerHTML =
                    '<p style="text-align:center; color:#b2bec3; font-size:0.8rem; margin-top:60px;">Belum ada materi ditambahkan.</p>';
                  isFirstItem = true;
                }
              });
            container.appendChild(div);
            inType.value = "";
            inTitle.value = "";
            inContent.value = "";
            inType.focus();
          };
        }
      },
      preConfirm: () => {
        const file = (document.getElementById("file-up") as HTMLInputElement)
          .files?.[0];
        const msg = (
          document.getElementById("swal-msg-up") as HTMLTextAreaElement
        ).value;
        const container = document.getElementById("list-up-container");
        let mats: any[] = [];
        container?.querySelectorAll(".mini-item").forEach((d: any) => {
          mats.push({
            week: d.dataset.week,
            type: d.dataset.type,
            title: d.dataset.title,
            content: d.dataset.content,
          });
        });

        if (!file) {
          Swal.showValidationMessage("File sertifikat wajib dipilih!");
          return false;
        }
        if (mats.length === 0) {
          Swal.showValidationMessage("Minimal input 1 materi/sesi!");
          return false;
        }
        return { file, msg, mats: JSON.stringify(mats) };
      },
    }).then(async (res) => {
      if (res.isConfirmed && res.value) {
        const fd = new FormData();
        fd.append("certificate", res.value.file);
        fd.append("materials_json", res.value.mats);
        fd.append("teacher_message", res.value.msg);
        try {
          MySwal.fire({
            title: "Mengupload...",
            didOpen: () => Swal.showLoading(),
          });
          const response = await fetch(
            `${BASE_URL}/certificates/${userId}/course/${courseId}/upload`,
            {
              method: "POST",
              headers: getAuthHeaders(),
              body: fd,
            },
          );
          const json = await response.json();
          if (json.success) {
            MySwal.fire(
              "Sukses",
              "Sertifikat & Materi berhasil disimpan!",
              "success",
            );
            fetchCourses();
          } else throw new Error(json.message);
        } catch (e: any) {
          MySwal.fire("Gagal", e.message, "error");
        }
      }
    });
  };

  // --- 3. FITUR VIEW & DELETE ---
  const handleViewCert = (file: string) =>
    window.open(getUploadUrl(`uploads/certificates/${file}`), "_blank");

  const handleDeleteCert = (userId: number, userName: string) => {
    MySwal.fire({
      title: `Hapus Sertifikat ${userName}?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Hapus",
      confirmButtonColor: "#d33",
    }).then(async (res) => {
      if (res.isConfirmed) {
        await fetch(`${BASE_URL}/certificates/${userId}`, {
          method: "DELETE",
          headers: getAuthHeaders(),
        });
        fetchCourses();
        MySwal.fire("Terhapus!", "", "success");
      }
    });
  };

  const filteredCourses = courses.filter((c) =>
    c.user_name.toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const getLevelClass = (level: string) => {
    if (!level) return "bg-none";
    if (level.toLowerCase().includes("basic")) return "bg-basic";
    if (level.toLowerCase().includes("independent")) return "bg-indep";
    if (level.toLowerCase().includes("proficient")) return "bg-prof";
    return "bg-none";
  };

  return (
    <>
      <style>{courseStyles}</style>
      <div className="course-container">
        <div className="course-header">
          <div className="title-group">
            <h3>Manajemen Kursus</h3>
            <div
              style={{
                color: "#636e72",
                fontSize: "0.85rem",
                marginTop: "5px",
              }}
            >
              <span>
                Total: {courses.length} | Aktif:{" "}
                {
                  courses.filter(
                    (c) => c.status === "Active" || c.status === "Completed",
                  ).length
                }
              </span>
            </div>
            <button
              onClick={fetchCourses}
              style={{
                marginTop: "10px",
                padding: "5px 15px",
                background: "#e53935",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
              }}
            >
              Refresh
            </button>
          </div>
          <div className="glass-search">
            <input
              type="text"
              placeholder="Cari siswa..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>Loading...</div>
        ) : (
          <div className="table-wrapper">
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Siswa</th>
                  <th>Tutor</th>
                  <th>Paket</th>
                  <th>Biaya</th>
                  <th>Sertifikat</th>
                  <th>Aksi</th>
                </tr>
              </thead>
              <tbody>
                {filteredCourses.map((course, index) => (
                  <tr key={index} className="course-row">
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                        }}
                      >
                        <div
                          className="avatar-small"
                          style={{
                            width: "40px",
                            height: "40px",
                            background: getAvatarColor(course.user_name),
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "white",
                            fontWeight: "bold",
                          }}
                        >
                          {getInitials(course.user_name)}
                        </div>
                        <div>
                          <h4 style={{ margin: 0, fontSize: "0.9rem" }}>
                            {course.user_name}
                          </h4>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "0.75rem",
                              color: "#636e72",
                            }}
                          >
                            {course.email}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td>{course.tutor_name || "-"}</td>
                    <td>
                      <span
                        className={`badge-capsule ${getLevelClass(course.level)}`}
                      >
                        {course.level}
                      </span>
                      <div style={{ fontSize: "0.75rem" }}>
                        {course.package}
                      </div>
                    </td>
                    <td>
                      {course.amount ? formatRupiah(course.amount) : "0"}
                      <br />
                      <span style={{ fontSize: "0.75rem" }}>
                        {course.session_count || 0} Sesi
                      </span>
                    </td>
                    <td>
                      {course.certificate_file ? (
                        <span style={{ color: "#00b894", fontWeight: "bold" }}>
                          Ada
                        </span>
                      ) : (
                        <span style={{ color: "#ccc" }}>Kosong</span>
                      )}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: "8px" }}>
                        {/* LOGIKA TOMBOL: Upload hanya muncul jika belum ada file. Edit/View/Del muncul jika sudah ada. */}
                        {!course.certificate_file ? (
                          <button
                            className="action-btn btn-upload"
                            onClick={() =>
                              handleUploadClick(
                                course.user_id,
                                course.user_name,
                                course.course_id,
                              )
                            }
                          >
                            <i className="fas fa-cloud-upload-alt"></i>
                          </button>
                        ) : (
                          <>
                            <button
                              onClick={() =>
                                quickUpdatePackage(
                                  course.user_id,
                                  course.user_name,
                                  course,
                                )
                              }
                              className="action-btn"
                              style={{
                                background: "#fff3e0",
                                color: "#f39c12",
                              }}
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button
                              className="action-btn"
                              onClick={() =>
                                handleViewCert(course.certificate_file!)
                              }
                              style={{
                                background: "#e0f2f1",
                                color: "#00b894",
                              }}
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button
                              className="action-btn btn-delete"
                              onClick={() =>
                                handleDeleteCert(
                                  course.user_id,
                                  course.user_name,
                                )
                              }
                            >
                              <i className="fas fa-trash-alt"></i>
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
};

export default CourseContent;
